(function () {
  // Bridges third-party "add to cart" actions (e.g. the Notify! wishlist app)
  // into the theme's cart UI.
  //
  // The theme's own add-to-cart emits the "cart-update" pub/sub event with
  // freshly rendered section HTML in the payload; the side-cart drawer, the
  // shipping-discount bar and the header cart count all key off that event.
  // A third-party app fires none of this, so its adds never reflect in the UI
  // until a reload.
  //
  // Strategy:
  //   1. Detect successful Shopify cart AJAX calls (fetch + XHR).
  //   2. Skip the theme's own adds (which already update the UI).
  //   3. Poll /cart.js until the change is actually committed (avoids the race
  //      where the drawer refreshes before the item lands → empty drawer).
  //   4. Fetch the rendered cart-drawer section and emit "cart-update" with the
  //      exact payload shape the theme expects, so every component updates.
  if (window.__cartAppBridgeInstalled) return;
  window.__cartAppBridgeInstalled = true;

  var THEME_RECENT_MS = 2000;
  var TRIGGER_DEBOUNCE_MS = 250;
  var POLL_TRIES = 6;
  var POLL_INTERVAL_MS = 200;

  var lastThemeCartUpdate = 0;
  var lastItemCount = null; // baseline used to detect a real change
  var syncing = false;
  var triggerTimer = null;

  function root() {
    var r =
      window.FoxThemeSettings &&
      window.FoxThemeSettings.routes &&
      window.FoxThemeSettings.routes.root;
    if (!r) return "/";
    return r.charAt(r.length - 1) === "/" ? r : r + "/";
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function fetchCart() {
    return fetch(root() + "cart.js", {
      headers: { Accept: "application/json" },
    }).then(function (r) {
      return r.json();
    });
  }

  function fetchDrawerSectionHTML() {
    return fetch(root() + "?section_id=cart-drawer").then(function (r) {
      return r.text();
    });
  }

  // The theme emits "cart-update" when it handles a cart change itself; record
  // it so we don't double-process the theme's own adds. Ignore our own emits.
  function onThemeCartUpdate(state) {
    if (state && state.source === "app-bridge") return;
    lastThemeCartUpdate = Date.now();
    if (state && typeof state.item_count === "number") {
      lastItemCount = state.item_count;
    }
  }

  function subscribeThemeEvents() {
    if (
      window.FoxThemeEvents &&
      typeof window.FoxThemeEvents.subscribe === "function"
    ) {
      window.FoxThemeEvents.subscribe("cart-update", onThemeCartUpdate);
      return true;
    }
    return false;
  }
  if (!subscribeThemeEvents()) {
    document.addEventListener("DOMContentLoaded", subscribeThemeEvents);
  }

  // Establish a baseline item count as early as possible.
  fetchCart()
    .then(function (cart) {
      if (lastItemCount === null) lastItemCount = cart.item_count;
    })
    .catch(function () {});

  function isCartMutationUrl(url) {
    if (!url) return false;
    var path;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch (e) {
      path = String(url);
    }
    return /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
  }

  async function syncFromApp() {
    if (syncing) return;
    // The theme just handled the cart itself; it already updated the UI.
    if (Date.now() - lastThemeCartUpdate < THEME_RECENT_MS) return;
    if (!document.getElementById("cart-drawer")) return;

    syncing = true;
    try {
      // Poll until the cart actually reflects the app's change (handles the
      // race where the app's request resolves slightly before the cart commits).
      var cart = null;
      var changed = false;
      for (var i = 0; i < POLL_TRIES; i++) {
        cart = await fetchCart();
        if (lastItemCount === null) {
          lastItemCount = cart.item_count;
        }
        if (cart.item_count !== lastItemCount) {
          changed = true;
          break;
        }
        await wait(POLL_INTERVAL_MS);
      }

      if (!cart) return;
      if (!changed) {
        // No detectable change (e.g. it was a no-op); keep baseline in sync.
        lastItemCount = cart.item_count;
        return;
      }
      lastItemCount = cart.item_count;

      var sectionHTML = await fetchDrawerSectionHTML();

      if (
        window.FoxThemeEvents &&
        typeof window.FoxThemeEvents.emit === "function"
      ) {
        // Same payload shape the theme's own add-to-cart emits: cart data plus
        // rendered sections. Drives the drawer, discount bar and cart count.
        // The "app-bridge" source tells the drawer to update silently WITHOUT
        // auto-opening, so it doesn't pop up underneath the app's own drawer
        // (e.g. the wishlist drawer) on add.
        var payload = Object.assign({}, cart, {
          source: "app-bridge",
          sections: { "cart-drawer": sectionHTML },
        });
        window.FoxThemeEvents.emit("cart-update", payload);
      } else {
        // Fallback if the theme event bus isn't available yet: refresh the
        // drawer contents without opening it.
        document.dispatchEvent(
          new CustomEvent("cart:refresh", { detail: { open: false } })
        );
      }
    } catch (e) {
      // Swallow — a failed bridge sync must never break the storefront.
    } finally {
      syncing = false;
    }
  }

  function triggerSync() {
    clearTimeout(triggerTimer);
    triggerTimer = setTimeout(syncFromApp, TRIGGER_DEBOUNCE_MS);
  }

  // Intercept fetch (used by most modern apps and the theme itself).
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      if (url && typeof url === "object" && url.url) url = url.url;
      return nativeFetch.apply(this, arguments).then(function (response) {
        if (response && response.ok && isCartMutationUrl(url)) {
          triggerSync();
        }
        return response;
      });
    };
  }

  // Intercept XMLHttpRequest (some apps still use it).
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartMutation = isCartMutationUrl(url);
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__cartMutation) {
      this.addEventListener("load", function () {
        if (this.status >= 200 && this.status < 300) {
          triggerSync();
        }
      });
    }
    return nativeSend.apply(this, arguments);
  };
})();
